import axios, { AxiosRequestConfig } from 'axios'

const ACCESS_TOKEN_KEY = 'portal_access_token'
const REFRESH_TOKEN_KEY = 'portal_refresh_token'

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

// Request interceptor — attach access token
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Track in-progress refresh to prevent parallel refresh storms
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

function processQueue(newToken: string) {
  refreshQueue.forEach((cb) => cb(newToken))
  refreshQueue = []
}

function doLogout(reason?: string) {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  window.location.href = reason ? `/login?reason=${reason}` : '/login'
}

// Response interceptor — auto-refresh on 401
axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest: AxiosRequestConfig & { _retry?: boolean } = error.config
    const errorCode: string = error.response?.data?.code ?? ''

    // Don't retry login or refresh endpoints
    const url: string = originalRequest.url ?? ''
    if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
      return Promise.reject(error.response?.data ?? { success: false, error: 'Network error' })
    }

    // ACCOUNT_DISABLED → immediate logout, no retry
    if (error.response?.status === 401 && errorCode === 'ACCOUNT_DISABLED') {
      doLogout('disabled')
      return Promise.reject(error.response?.data)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

      if (!refreshToken) {
        doLogout()
        return Promise.reject(error.response?.data ?? { success: false, error: 'Session expired' })
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((newToken) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
            }
            resolve(axiosInstance(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const result = await axiosInstance.post<any>('/api/auth/refresh', { refreshToken })
        const { accessToken: newAccess, refreshToken: newRefresh, user: newUser, modules: newModules } = (result as any).data

        localStorage.setItem(ACCESS_TOKEN_KEY, newAccess)
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh)

        // Notify authStore via window event (avoids circular dependency)
        window.dispatchEvent(
          new CustomEvent('portal:auth-refreshed', {
            detail: { accessToken: newAccess, user: newUser, modules: newModules ?? [] },
          }),
        )

        processQueue(newAccess)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccess}`
        }
        return axiosInstance(originalRequest)
      } catch {
        doLogout()
        return Promise.reject({ success: false, error: 'Session expired' })
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error.response?.data ?? { success: false, error: 'Network error' })
  },
)

export default axiosInstance
